import { Stack } from '@mui/material';
import { useMemo } from 'react';
import {
  IMPORT_MORE,
  useDtoolsImport,
} from 'src/components/ImportDtoolsProjectModal/dtools-import-context';
import { ImportJobDialog } from 'src/components/ImportDtoolsProjectModal/import-job-dialog';
import { ImportPreviewList } from 'src/components/ImportProposalsModal/import-preview-list';
import { formatProjectCount } from 'src/components/ImportProposalsModal/import-results.utils';
import { ImportSummary } from 'src/components/ImportProposalsModal/import-summary';
import { ModalStep, useModalStep, useModalSteps } from 'src/components/ModalContainer';

/** Step 2: what the dry run would create and update; opens while the dry run still runs. */
export const ProjectsSummary = () => {
  const { pushStep } = useModalSteps();
  const { popStep } = useModalStep();
  const {
    previewProjects: projects,
    previewOutcome: preview,
    isStartingJob,
    hasImportStarted,
    importPreviewed,
    clearImport,
    close,
  } = useDtoolsImport();
  const projectNames = useMemo(
    () => new Map(projects.map((p) => [p.id.toString(), p.name])),
    [projects],
  );
  const { records, errors, hasMoreResults, loadMoreResults } = preview;
  const loading = preview.loading || !preview.isFinished;
  const projectsLabel = formatProjectCount(projects.length);

  const handleImport = async () => {
    if (!(await importPreviewed())) {
      return;
    }
    const result = await pushStep(<ImportJobDialog />);
    if (result === IMPORT_MORE) {
      clearImport();
      popStep(IMPORT_MORE);
    }
  };

  return (
    <ModalStep
      title="Review import"
      actions={[
        {
          label: 'Cancel',
          kind: 'secondary',
          onClick: close,
          testId: 'import-dtools-projects-preview-cancel',
        },
        {
          label: errors?.length ? 'Import anyway' : `Import ${projectsLabel}`,
          kind: 'primary',
          onClick: () => void handleImport(),
          disabled: projects.length === 0 || loading || hasImportStarted,
          loading: isStartingJob,
          testId: 'import-dtools-projects-preview-import',
        },
      ]}
    >
      <Stack gap={1.5} data-testid="import-dtools-projects-preview">
        <ImportSummary
          status={loading ? `Checking ${projectsLabel}…` : `${projectsLabel} to import`}
          records={loading || !records.length ? undefined : records}
          tense="preview"
          testId="import-dtools-projects-preview"
        />
        <ImportPreviewList
          records={records}
          errors={errors}
          projectNames={projectNames}
          loading={loading}
          loadMore={hasMoreResults ? { onSelect: loadMoreResults } : undefined}
          testId="import-dtools-projects-preview"
        />
      </Stack>
    </ModalStep>
  );
};
